import { createApp, nextTick, type App } from 'vue';
import LenseDraw from '@/components/sections/orderLenses/orderPartials/LenseDraw.vue';
import LenseSection from '@/components/sections/orderLenses/orderPartials/LenseSection.vue';

/** Un solo LoadInfo compartido por los LenseDraw off-screen (mismo VP que el formulario). */
async function loadAjustesLente(): Promise<Record<string, unknown> & { VALOR_PANTALLA: number }> {
  const fallback = { VALOR_PANTALLA: 68 };
  try {
    const { useOfertasStore } = await import('@/stores/ofertas');
    const data = await useOfertasStore().LoadInfo();
    if (data && typeof data === 'object' && (data as { VALOR_PANTALLA?: number }).VALOR_PANTALLA) {
      return data as Record<string, unknown> & { VALOR_PANTALLA: number };
    }
  } catch {
    /* usar fallback */
  }
  return fallback;
}

/**
 * Renderiza los componentes LenseDraw y LenseSection off-screen
 * y captura el HTML generado para usar en PDFs.
 *
 * @param pedido - Datos del pedido con información de las lentes
 * @param store - Store de Vuex (necesario para que los componentes tengan acceso a getters)
 * @returns HTML string con los SVGs renderizados
 */
export async function renderLensesOffscreen(pedido: any, store?: any): Promise<string> {
  // Crear contenedor temporal oculto
  const container = document.createElement('div');
  container.id = 'importador-imagenes-temp';
  container.style.cssText = `
    position: absolute;
    top: -9999px;
    left: -9999px;
    visibility: hidden;
  `;
  container.className = 'datos-tecnicos-svg-group flex flex-row items-center justify-center gap-0';
  document.body.appendChild(container);

  try {
    const ajustes = await loadAjustesLente();
    // Preparar datos de los ojos desde el pedido
    const odData = prepareEyeData(pedido, 'od');
    const oiData = prepareEyeData(pedido, 'oi');
    const numeroPuntos = 600;

    // Array para almacenar las instancias de Vue
    const instances: App[] = [];

    // Montar LenseSection para OD
    const sectionODMount = document.createElement('div');
    sectionODMount.style.marginRight = '-0.35rem';
    container.appendChild(sectionODMount);
    const sectionOD = createApp(LenseSection, {
      eye: odData
    });
    sectionOD.provide('store', store);
    sectionOD.mount(sectionODMount);
    instances.push(sectionOD);

    // Montar LenseDraw para OD
    const drawODMount = document.createElement('div');
    drawODMount.style.marginRight = '-0.75rem';
    container.appendChild(drawODMount);
    const drawOD = createApp(LenseDraw, {
      eye: odData,
      numeroPuntos: numeroPuntos,
      initialAjustes: ajustes,
    });
    drawOD.provide('store', store);
    drawOD.mount(drawODMount);
    instances.push(drawOD);

    // Montar LenseDraw para OI
    const drawOIMount = document.createElement('div');
    drawOIMount.style.marginLeft = '-0.75rem';
    container.appendChild(drawOIMount);
    const drawOI = createApp(LenseDraw, {
      eye: oiData,
      numeroPuntos: numeroPuntos,
      initialAjustes: ajustes,
    });
    drawOI.provide('store', store);
    drawOI.mount(drawOIMount);
    instances.push(drawOI);

    // Montar LenseSection para OI
    const sectionOIMount = document.createElement('div');
    sectionOIMount.style.marginLeft = '-0.35rem';
    container.appendChild(sectionOIMount);
    const sectionOI = createApp(LenseSection, {
      eye: oiData
    });
    sectionOI.provide('store', store);
    sectionOI.mount(sectionOIMount);
    instances.push(sectionOI);

    // Esperar a que Vue termine de renderizar
    await nextTick();

    // Espera más larga para asegurar que los componentes ejecuten mounted() y calculated()
    await new Promise(resolve => setTimeout(resolve, 300));
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 400));

    // Capturar el HTML del contenedor
    const html = container.innerHTML;

    // Limpiar todas las instancias
    instances.forEach(instance => {
      instance.unmount();
    });

    // Remover el contenedor del DOM
    document.body.removeChild(container);

    return html;

  } catch (error) {
    // En caso de error, asegurar limpieza
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
    throw error;
  }
}

/**
 * Prepara los datos del ojo en el formato esperado por los componentes
 */
function prepareEyeData(pedido: any, eyeName: 'od' | 'oi') {
  const isOD = eyeName === 'od';
  let eyeOut = isOD ? pedido?.ojoOutDerecho : pedido?.ojoOutIzquierdo;
  // Obtener datos de INPUT - buscar en shortcuts y también en listOJOIN
  let eyeIn = isOD ? pedido?.oClaseBAPI?.ojoInDerecho : pedido?.oClaseBAPI?.ojoInIzquierdo;
  if (!eyeIn) {
    const listOJOIN = pedido?.oClaseBAPI?.listOJOIN;
    const inputArray = Array.isArray(listOJOIN) ? listOJOIN
      : Array.isArray(listOJOIN?.clsZES_OJO_INPUT) ? listOJOIN.clsZES_OJO_INPUT
      : [];
    eyeIn = inputArray.find((o: any) => o.ZOJO === (isOD ? 'D' : 'I'));
  }

  // Obtener datos de OUTPUT - buscar en ambos formatos (array directo o wrapped en clsZES_OJO_OUTPUT)
  const listOJOOUT = pedido?.oClaseBAPI?.listOJOOUT;
  const outputArray = Array.isArray(listOJOOUT) ? listOJOOUT
    : Array.isArray(listOJOOUT?.clsZES_OJO_OUTPUT) ? listOJOOUT.clsZES_OJO_OUTPUT
    : [];
  const outputData = outputArray.find(
    (o: any) => o.ZOJO === (isOD ? 'D' : 'I')
  );

  // Si eyeOut no existe, usar outputData como fallback
  if (!eyeOut && outputData) {
    eyeOut = { ...outputData };
  }

  // Calcular radio de lentilla con valores por defecto seguros
  // Usar escala por defecto si no se especifica en ajustes
  const escala = 1.35; // Valor por defecto
  const mmToPx = (mm: number) => {
    if (isNaN(mm) || !isFinite(mm)) {
      return 0;
    }
    return (96 * mm) / 25.4;
  };

  const lentillaRadioCalculado = mmToPx(2.5) / escala;
  // Validar que el radio calculado es válido
  const lentillaRadio = (lentillaRadioCalculado > 0 &&
                         !isNaN(lentillaRadioCalculado) &&
                         isFinite(lentillaRadioCalculado))
                         ? lentillaRadioCalculado
                         : 5;

  // Parsear valores numéricos de forma segura
  const parseFloatSafe = (value: any, defaultValue: number = 0): number => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  const eyeData = {
    name: eyeName,
    viewBox: eyeOut?.viewBox || '0 0 0 0',
    width: parseFloatSafe(eyeOut?.width, 15),
    height: parseFloatSafe(eyeOut?.height, 15),
    path: eyeOut?.path || '',
    stroke: '#A5CAD8',
    strokeWidth: 0.8,
    bgColor: 'none',
    baseText: eyeOut?.TXT_LENT || '',
    FormaRO: pedido?.oClaseBAPI?.[isOD ? 'FormaROD' : 'FormaROI'] || '',
    FormaSO: pedido?.oClaseBAPI?.[isOD ? 'FormaSOD' : 'FormaSOI'] || '',
    FormaEO: pedido?.oClaseBAPI?.[isOD ? 'FormaEOD' : 'FormaEOI'] || '',
    FormaMO: pedido?.oClaseBAPI?.[isOD ? 'FormaMOD' : 'FormaMOI'] || '',
    FormaNO: pedido?.oClaseBAPI?.[isOD ? 'FormaNOD' : 'FormaNOI'] || '',
    angulo: parseFloatSafe(eyeOut?.angulo, 0),
    espesor: parseFloatSafe(eyeOut?.ESPESOR, 0),
    radio: parseFloatSafe(eyeOut?.radio, 0),
    initialAngle: isOD ? 180 : 0,
    f1: outputData ? parseFloatSafe(outputData.F1, 0).toFixed(2) : '0.0',
    f2: outputData ? parseFloatSafe(outputData.F2, 0).toFixed(2) : '0.0',
    adicX: outputData ? parseFloatSafe(outputData.ADIC_POSX, 0).toFixed(2) : '0.0',
    adicY: outputData ? parseFloatSafe(outputData.ADIC_POSY, 0).toFixed(2) : '0.0',
    focoMaterial: eyeIn?.FOCO_MATERIAL || null,
    desplazamiento: parseFloatSafe(eyeOut?.desplazamiento, 0),
    DIAMETRO: eyeOut?.DIAMETRO || '62/62',
    ESPESOR: parseFloatSafe(eyeOut?.ESPESOR, 0),
    ESP_MIN: parseFloatSafe(eyeOut?.ESP_MIN, 0),
    ESP_MAX: parseFloatSafe(eyeOut?.ESP_MAX, 0),
    TAL_PUNTOS: eyeOut?.TAL_PUNTOS || '',
    TAL_SECUENCIA: eyeOut?.TAL_SECUENCIA || '',
    // Proporcionar valor seguro para lentilla.radio para evitar valores negativos
    // El mínimo debe ser > 1 porque en el SVG se usa radio - 0.8
    lentilla: {
      x: 0,
      y: 0,
      radio: lentillaRadio >= 1 ? lentillaRadio : 5 // Valor mínimo seguro de 5
    }
  };

  // Validación final: verificar que todos los valores numéricos críticos son válidos
  if (isNaN(eyeData.lentilla.radio) || eyeData.lentilla.radio < 1) {
    eyeData.lentilla.radio = 5;
  }


  return eyeData;
}

/**
 * Función alternativa que crea un contenedor con el ID original
 * para mantener compatibilidad con el código existente de html2canvas
 *
 * @param pedido - Datos del pedido con información de las lentes
 * @param store - Store de Vuex (necesario para que los componentes tengan acceso a getters)
 */
export async function renderLensesOffscreenWithId(pedido: any, store?: any, soloDibujos: boolean = false): Promise<HTMLElement> {
  const container = document.createElement('div');
  container.id = 'importador-imagenes';
  // Ocultar completamente el contenedor visualmente pero mantenerlo accesible para html2canvas
  // html2canvas puede capturar elementos fuera de pantalla pero necesitan estar en el DOM
  container.style.cssText = `
    position: absolute;
    top: -9999px;
    left: -9999px;
    width: auto;
    height: auto;
    background: white;
    padding: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 20px;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
  `;
  container.className = 'datos-tecnicos-svg-group flex flex-row items-center justify-center gap-0';
  document.body.appendChild(container);

  try {

    const ajustes = await loadAjustesLente();

    // Cargar caracteristicas de lente para que LenseDraw tenga cruzMontajeX/Y correctos
    try {
      // Buscar VKORG en CABOUT (shortcut) o en listCABOUT
      const cabout = pedido?.oClaseBAPI?.CABOUT
        || (Array.isArray(pedido?.oClaseBAPI?.listCABOUT) ? pedido.oClaseBAPI.listCABOUT[0] : null)
        || pedido?.oClaseBAPI?.listCABOUT?.clsZES_CAB_OUTPUT?.[0];
      const orgVenta = cabout?.VKORG;
      const cliente = pedido?.oClaseBAPI?.CLIENTE;
      // Buscar ojoIn en shortcuts o en listOJOIN
      let ojoIn = pedido?.oClaseBAPI?.ojoInDerecho || pedido?.oClaseBAPI?.ojoInIzquierdo;
      if (!ojoIn) {
        const listOJOIN = pedido?.oClaseBAPI?.listOJOIN;
        const inputArr = Array.isArray(listOJOIN) ? listOJOIN
          : Array.isArray(listOJOIN?.clsZES_OJO_INPUT) ? listOJOIN.clsZES_OJO_INPUT
          : [];
        ojoIn = inputArr[0];
      }
      const lente = ojoIn?.JER_EDI_INET;
      if (orgVenta && cliente && lente) {
        const { useOfertasStore } = await import('@/stores/ofertas');
        await useOfertasStore().GetCaracteristicasLente({
          payload: { Vkorg: orgVenta, Cliente: cliente, Lente: lente }
        });
      }
    } catch (error) {
    }

    let pedidoConFormas = pedido;

    // Extraer FormaROD/FormaROI de listFORMAS si están disponibles
    // recuperarPedido devuelve las formas en listFORMAS pero no las mapea a FormaROD/FormaROI
    if (!pedido?.oClaseBAPI?.FormaROD && !pedido?.oClaseBAPI?.FormaROI) {
      const listFORMAS = pedido?.oClaseBAPI?.listFORMAS;
      const formasArray = Array.isArray(listFORMAS) ? listFORMAS
        : Array.isArray(listFORMAS?.clsZFORMAS) ? listFORMAS.clsZFORMAS
        : [];

      if (formasArray.length > 0) {
        const tipoMap: Record<string, string> = { R: 'FormaRO', S: 'FormaSO', E: 'FormaEO', M: 'FormaMO', N: 'FormaNO' };
        for (const forma of formasArray) {
          const prefix = tipoMap[forma.TIPOPUNTOS];
          if (!prefix) continue;
          const suffix = forma.LENTE === 'D' ? 'D' : forma.LENTE === 'I' ? 'I' : null;
          if (!suffix) continue;
          const key = `${prefix}${suffix}`;
          if (forma.PUNTOS && !pedidoConFormas.oClaseBAPI[key]) {
            pedidoConFormas.oClaseBAPI[key] = forma.PUNTOS;
          }
        }
      }
    }

    // Si no tiene FormaROD/FormaROI, necesitamos preparar el pedido
    if (!pedidoConFormas?.oClaseBAPI?.FormaROD && !pedidoConFormas?.oClaseBAPI?.FormaROI) {

      try {
        // Normalizar la estructura antes de enviar
        const pedidoParaEnviar = { ...pedido.oClaseBAPI };

        // Asegurar que las listas tengan la estructura correcta
        if (!pedidoParaEnviar.listCABIN?.clsZES_CAB_INPUT) {
          pedidoParaEnviar.listCABIN = { clsZES_CAB_INPUT: [] };
        }
        if (!pedidoParaEnviar.listCABOUT?.clsZES_CAB_OUTPUT) {
          pedidoParaEnviar.listCABOUT = { clsZES_CAB_OUTPUT: [] };
        }
        if (!pedidoParaEnviar.listCABFRM?.clsZES_CAB_FORMA) {
          pedidoParaEnviar.listCABFRM = { clsZES_CAB_FORMA: [] };
        }
        if (!pedidoParaEnviar.listOJOIN?.clsZES_OJO_INPUT) {
          pedidoParaEnviar.listOJOIN = { clsZES_OJO_INPUT: [] };
        }
        if (!pedidoParaEnviar.listOJOOUT?.clsZES_OJO_OUTPUT) {
          pedidoParaEnviar.listOJOOUT = { clsZES_OJO_OUTPUT: [] };
        }
        if (!pedidoParaEnviar.listFORMAS?.clsZFORMAS) {
          pedidoParaEnviar.listFORMAS = { clsZFORMAS: [] };
        }
        if (!pedidoParaEnviar.listTALADROS?.clsZES_APLT_TLDROS) {
          pedidoParaEnviar.listTALADROS = { clsZES_APLT_TLDROS: [] };
        }
        if (!pedidoParaEnviar.listBASES?.clsZES_BASENOM) {
          pedidoParaEnviar.listBASES = { clsZES_BASENOM: [] };
        }
        if (!pedidoParaEnviar.listTRETURN) {
          pedidoParaEnviar.listTRETURN = [];
        }


        // FIX 1: Si falta ALTURA_BOXING, calcularla o usar un valor por defecto
        if (pedidoParaEnviar.CABFRM && !pedidoParaEnviar.CABFRM.ALTURA_BOXING) {
          const calibre = parseFloat(pedidoParaEnviar.CABFRM.CALIBRE) || 50;
          // Altura boxing típicamente es ~85% del calibre para formas ovaladas
          const alturaCalculada = Math.round(calibre * 0.85);
          pedidoParaEnviar.CABFRM.ALTURA_BOXING = alturaCalculada.toString();
        }

        // FIX 2: CRÍTICO - Copiar CABFRM a listCABFRM.clsZES_CAB_FORMA
        // El backend NO lee CABFRM, lee listCABFRM.clsZES_CAB_FORMA[0]
        if (pedidoParaEnviar.CABFRM) {
          if (!pedidoParaEnviar.listCABFRM) {
            pedidoParaEnviar.listCABFRM = { clsZES_CAB_FORMA: [] };
          }
          if (!pedidoParaEnviar.listCABFRM.clsZES_CAB_FORMA ||
              pedidoParaEnviar.listCABFRM.clsZES_CAB_FORMA.length === 0) {
            pedidoParaEnviar.listCABFRM.clsZES_CAB_FORMA = [{ ...pedidoParaEnviar.CABFRM }];
          }
        }

        // FIX 3: Copiar ojoInDerecho/ojoInIzquierdo a listOJOIN.clsZES_OJO_INPUT
        const ojos = [];
        if (pedidoParaEnviar.ojoInDerecho) {
          ojos.push({ ...pedidoParaEnviar.ojoInDerecho });
        }
        if (pedidoParaEnviar.ojoInIzquierdo) {
          ojos.push({ ...pedidoParaEnviar.ojoInIzquierdo });
        }
        if (ojos.length > 0) {
          if (!pedidoParaEnviar.listOJOIN) {
            pedidoParaEnviar.listOJOIN = { clsZES_OJO_INPUT: [] };
          }
          if (!pedidoParaEnviar.listOJOIN.clsZES_OJO_INPUT ||
              pedidoParaEnviar.listOJOIN.clsZES_OJO_INPUT.length === 0) {
            pedidoParaEnviar.listOJOIN.clsZES_OJO_INPUT = ojos;
          }
        }

        // FIX 4: Copiar CABIN a listCABIN.clsZES_CAB_INPUT
        if (pedidoParaEnviar.CABIN) {
          if (!pedidoParaEnviar.listCABIN) {
            pedidoParaEnviar.listCABIN = { clsZES_CAB_INPUT: [] };
          }
          if (!pedidoParaEnviar.listCABIN.clsZES_CAB_INPUT ||
              pedidoParaEnviar.listCABIN.clsZES_CAB_INPUT.length === 0) {
            pedidoParaEnviar.listCABIN.clsZES_CAB_INPUT = [{ ...pedidoParaEnviar.CABIN }];
          }
        }

        // Llamar a la acción que calcula las formas
        const { useIndoStore } = await import('@/stores/indo');
        const pedidoPreparado = await useIndoStore().PrepararPedido({
          pedido: pedidoParaEnviar
        });


        // Mostrar TODOS los mensajes de TRETURN para debug
        if (pedidoPreparado?.oClaseBAPI?.listTRETURN && pedidoPreparado.oClaseBAPI.listTRETURN.length > 0) {
        }

        if (pedidoPreparado?.oClaseBAPI?.FormaROD || pedidoPreparado?.oClaseBAPI?.FormaROI) {
          pedidoConFormas = pedidoPreparado;
        } else {
        }
      } catch (error) {

        // A pesar del error, el backend puede haber calculado las formas
        // Intentar obtener el nuevoPedido del store que puede tener las formas
        const { useIndoStore: useIndoStoreLocal } = await import('@/stores/indo');
        const pedidoStore = useIndoStoreLocal().pedido;


        if (pedidoStore?.oClaseBAPI?.FormaROD || pedidoStore?.oClaseBAPI?.FormaROI) {
          pedidoConFormas = pedidoStore;
        } else {
        }
      }
    } else {
    }

    const odData = prepareEyeData(pedidoConFormas, 'od');
    const oiData = prepareEyeData(pedidoConFormas, 'oi');
    const numeroPuntos = 600;


    const instances: App[] = [];

    // Si soloDibujos es true, solo montar LenseDraw (sin LenseSection)
    if (soloDibujos) {
      const drawODMount = document.createElement('div');
      drawODMount.style.marginRight = '-0.75rem';
      container.appendChild(drawODMount);
      const drawOD = createApp(LenseDraw, { eye: odData, numeroPuntos, initialAjustes: ajustes, showCaption: false });
      drawOD.provide('store', store);
      drawOD.mount(drawODMount);
      instances.push(drawOD);
      await nextTick();

      const drawOIMount = document.createElement('div');
      drawOIMount.style.marginLeft = '-0.75rem';
      container.appendChild(drawOIMount);
      const drawOI = createApp(LenseDraw, { eye: oiData, numeroPuntos, initialAjustes: ajustes, showCaption: false });
      drawOI.provide('store', store);
      drawOI.mount(drawOIMount);
      instances.push(drawOI);
      await nextTick();
    } else {
      // Montar componentes en el mismo orden que FormsThickness
      const sectionODMount = document.createElement('div');
      sectionODMount.style.marginRight = '-0.35rem';
      container.appendChild(sectionODMount);
      const sectionOD = createApp(LenseSection, { eye: odData });
      sectionOD.provide('store', store);
      sectionOD.mount(sectionODMount);
      instances.push(sectionOD);
      await nextTick();

      const drawODMount = document.createElement('div');
      drawODMount.style.marginRight = '-0.75rem';
      container.appendChild(drawODMount);
      const drawOD = createApp(LenseDraw, { eye: odData, numeroPuntos, initialAjustes: ajustes });
      drawOD.provide('store', store);
      drawOD.mount(drawODMount);
      instances.push(drawOD);
      await nextTick();

      const drawOIMount = document.createElement('div');
      drawOIMount.style.marginLeft = '-0.75rem';
      container.appendChild(drawOIMount);
      const drawOI = createApp(LenseDraw, { eye: oiData, numeroPuntos, initialAjustes: ajustes });
      drawOI.provide('store', store);
      drawOI.mount(drawOIMount);
      instances.push(drawOI);
      await nextTick();

      const sectionOIMount = document.createElement('div');
      sectionOIMount.style.marginLeft = '-0.35rem';
      container.appendChild(sectionOIMount);
      const sectionOI = createApp(LenseSection, { eye: oiData });
      sectionOI.provide('store', store);
      sectionOI.mount(sectionOIMount);
      instances.push(sectionOI);
      await nextTick();
    }

    // Esperar a que los LenseDraw acaben de poblar su path SVG (init()
    // ejecutado y `pathData` reactivo aplicado). Antes había 3 × 500 ms
    // hardcoded "por si acaso"; ahora la condición se chequea por rAF y se
    // resuelve en cuanto está lista, con un timeout duro como red de seguridad.
    await nextTick();
    await waitForLensesReady(container);

    // Guardar las instancias en el contenedor para limpiarlas después
    (container as any).__vueInstances = instances;

    return container;

  } catch (error) {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
    throw error;
  }
}

/**
 * Espera (por `requestAnimationFrame`) a que todos los `<path class="horizontal-gradient">`
 * dentro del contenedor tengan un atributo `d` no vacío — señal inequívoca de
 * que cada `LenseDraw` ejecutó `init()` y Vue aplicó la actualización reactiva.
 *
 * Sustituye los `setTimeout` arbitrarios: resuelve en cuanto las lentes están
 * dibujadas (típicamente 1-2 frames) y, si por alguna razón no se completa,
 * cae al timeout duro pasado por parámetro como red de seguridad.
 */
export function waitForLensesReady(container: HTMLElement, timeoutMs = 1500): Promise<void> {
  return new Promise<void>((resolve) => {
    const start = performance.now();
    const check = () => {
      const paths = container.querySelectorAll<SVGPathElement>("path.horizontal-gradient");
      const ready = paths.length > 0
        && Array.from(paths).every((p) => (p.getAttribute("d") || "").length > 0);
      if (ready || performance.now() - start >= timeoutMs) {
        resolve();
        return;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

/**
 * Limpia el contenedor creado por renderLensesOffscreenWithId
 */
export function cleanupOffscreenContainer(container: HTMLElement) {
  try {
    const instances = (container as any).__vueInstances as App[];
    if (instances) {
      instances.forEach(instance => instance.unmount());
    }
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  } catch (error) {
  }
}
